import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { VRMLLoader } from 'three/examples/jsm/loaders/VRMLLoader.js';
import { FikoObje3D } from './FikoObje3D';
import { FikoSahne } from './FikoSahne';
import { FikoCizimWRL } from './FikoThreeCam/FikoCizimWRL';
import { FikoVektor3D } from './FikoVektor3d';


export interface FikoWRLHiyerarsi {
    name: string;
    type: string;
    children?: FikoWRLHiyerarsi[];
}


export class FikoImporterFikoJson {

    noktalar: THREE.BufferGeometry[] = []
    edges: THREE.BufferGeometry[] = [];
    cizgiNoktalari: THREE.BufferGeometry[] = [];
    yuzeyler: THREE.BufferGeometry[] = [];

    public onProgress?: (mesaj: string, progress: number) => void;

    constructor(public sahne?: FikoSahne) {

    }

    grpi = 0;
    strti = 0;
    grplinei = 0;
    strtlinei = 0;

    async alDosyadan1(dosya: any): Promise<{
        yuzeyler: THREE.BufferGeometry,
        cizgiler: THREE.BufferGeometry,
        vertex: THREE.BufferGeometry,
        faceBrep?: any[],
        edgeBrep?: any[]
    }> {
        return new Promise((resolve, reject) => {
            this.noktalar = [];
            this.edges = [];
            this.cizgiNoktalari = [];
            this.yuzeyler = [];

            this.grpi = 0;
            this.strti = 0;
            this.grplinei = 0;
            this.strtlinei = 0;

            this.onProgress?.('JSON dosyası yükleniyor...', 0);

            const processJson = (jsonString: string) => {
                try {
                    const json = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    const vertices = json.vertices || [];
                    const faces = json.faces?.triangles || [];
                    const faceColors = json.faces?.color || [];
                    const faceAlpha = json.faces?.alpha || [];
                    const faceGrup = json.faces?.grup || [];
                    const faceBrep = json.faces?.brep || [];

                    // Raw edge data - her edge'in kendi lines'ı var
                    const rawEdges = json.edges || [];
                    const points = json.points || [];

                    // Edge'leri merge et ve group oluştur (WRL format uyumlu)
                    let mergedEdgeLines: [number, number][] = [];
                    let mergedEdgeColors: number[] = [];
                    let mergedEdgeAlpha: number[] = [];
                    let mergedEdgeGrup: number[] = []; // [start_idx, vertex_count, group_id] per vertex
                    let mergedEdgeBrep: any[] = [];

                    let strtlinei = 0; // Line başlangıç index'i (WRL uyumlu)
                    let grplinei = faceBrep.length; // Face gruplarından sonra başla

                    for (let ei = 0; ei < rawEdges.length; ei++) {
                        const edgeObj = rawEdges[ei];
                        const edgeLines: [number, number][] = edgeObj.lines || [];

                        // VALIDATION: sadece valid segments'i tutmak
                        const validSegments: [number, number][] = [];
                        for (const line of edgeLines) {
                            if (line[0] >= 0 && line[1] >= 0 && line[0] < vertices.length && line[1] < vertices.length) {
                                validSegments.push(line);
                            }
                        }

                        const numVertices = validSegments.length * 2; // Her segment 2 vertex

                        // Skip empty edges
                        if (numVertices === 0) {
                            mergedEdgeBrep.push({
                                index: edgeObj.index,
                                type: edgeObj.type,
                                ...edgeObj
                            });
                            grplinei++;
                            continue;
                        }

                        // Her valid segment'i process et
                        for (let si = 0; si < validSegments.length; si++) {
                            const line = validSegments[si];
                            mergedEdgeLines.push(line);

                            // Her vertex için renk, alpha ve grup info (WRL uyumlu)
                            for (let vi = 0; vi < 2; vi++) {
                                mergedEdgeColors.push(0.2, 0.2, 0.2); // Koyu gri
                                mergedEdgeAlpha.push(1.0);

                                // Grup: [strtlinei, vertex_count, grplinei] - WRL format
                                mergedEdgeGrup.push(strtlinei, numVertices, grplinei);
                            }
                        }

                        // BREP bilgisini ekle
                        mergedEdgeBrep.push({
                            index: edgeObj.index,
                            type: edgeObj.type,
                            ...edgeObj
                        });

                        // WRL uyumlu index artış
                        strtlinei += numVertices;
                        grplinei++;
                    }

                    this.onProgress?.('Geometriler oluşturuluyor...', 20);

                    // ---------- Yüzeyler ----------
                    if (faces.length > 0) {
                        const geom = new THREE.BufferGeometry();
                        const posArray: number[] = [];
                        const colorArray: number[] = [];
                        const alphaArray: number[] = [];
                        const grupArray: number[] = [];

                        for (let i = 0; i < faces.length; i++) {
                            const tri = faces[i];
                            for (let j = 0; j < 3; j++) {
                                const vid = tri[j];
                                const v = vertices[vid];
                                posArray.push(v[0], v[1], v[2]);

                                colorArray.push(
                                    faceColors[vid * 3 + 0] ?? 0.8,
                                    faceColors[vid * 3 + 1] ?? 0.8,
                                    faceColors[vid * 3 + 2] ?? 0.8
                                );
                                alphaArray.push(faceAlpha[vid] ?? 1.0);

                                const gi = vid * 3;
                                grupArray.push(
                                    faceGrup[gi + 0] ?? 0,
                                    faceGrup[gi + 1] ?? 0,
                                    faceGrup[gi + 2] ?? 0
                                );
                            }
                        }

                        geom.setAttribute('position', new THREE.Float32BufferAttribute(posArray, 3));
                        geom.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
                        geom.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArray, 1));
                        geom.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3));

                        this.yuzeyler.push(geom);
                    }

                    // ---------- Çizgiler (Merged) ----------
                    if (mergedEdgeLines.length > 0) {
                        const geom = new THREE.BufferGeometry();
                        const posArray: number[] = [];
                        const colorArray: number[] = [];
                        const alphaArray: number[] = [];
                        const grupArray: number[] = []; // [start_idx, segment_count, group_id] per vertex

                        // Artık tüm lines valid (merge build'de validation yapıldı)
                        for (let i = 0; i < mergedEdgeLines.length; i++) {
                            const e = mergedEdgeLines[i];
                            const p0 = vertices[e[0]];
                            const p1 = vertices[e[1]];

                            // Position
                            posArray.push(p0[0], p0[1], p0[2]);
                            posArray.push(p1[0], p1[1], p1[2]);

                            // Color, alpha ve grup - index'leme garanti doğru
                            // Her line'ın 2 vertex'i var
                            for (let vi = 0; vi < 2; vi++) {
                                const vertexIdx = i * 2 + vi;
                                const colorIdx = vertexIdx * 3;
                                const alphaIdx = vertexIdx;
                                const grupIdx = vertexIdx * 3; // 3 values per vertex

                                // Color (RGB)
                                colorArray.push(
                                    mergedEdgeColors[colorIdx],
                                    mergedEdgeColors[colorIdx + 1],
                                    mergedEdgeColors[colorIdx + 2]
                                );

                                // Alpha
                                alphaArray.push(mergedEdgeAlpha[alphaIdx]);

                                // Grup: [start_idx, segment_count, group_id] - Range selection
                                grupArray.push(
                                    mergedEdgeGrup[grupIdx],
                                    mergedEdgeGrup[grupIdx + 1],
                                    mergedEdgeGrup[grupIdx + 2]
                                );
                            }
                        }

                        geom.setAttribute('position', new THREE.Float32BufferAttribute(posArray, 3));
                        geom.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
                        geom.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArray, 1));
                        geom.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3)); // [start, count, id]

                        this.edges.push(geom);
                    }

                    // ---------- Noktalar ----------
                    if (points.length > 0) {
                        const geom = new THREE.BufferGeometry();
                        const pos: number[] = [];
                        for (const p of points) pos.push(p[0], p[1], p[2]);
                        geom.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
                        this.noktalar.push(geom);
                    }

                    this.onProgress?.('Geometriler birleştiriliyor...', 60);

                    const mergedNoktalar = this.noktalar.length > 0 ? BufferGeometryUtils.mergeGeometries(this.noktalar) : new THREE.BufferGeometry();
                    const mergedCizgiNoktalari = this.cizgiNoktalari.length > 0 ? BufferGeometryUtils.mergeGeometries(this.cizgiNoktalari) : new THREE.BufferGeometry();
                    const mergedCizgiler = this.edges.length > 0 ? BufferGeometryUtils.mergeGeometries(this.edges) : new THREE.BufferGeometry();
                    const mergedYuzeyler = this.yuzeyler.length > 0 ? BufferGeometryUtils.mergeGeometries(this.yuzeyler) : new THREE.BufferGeometry();

                    this.onProgress?.('JSON yükleme tamamlandı', 100);

                    resolve({
                        yuzeyler: mergedYuzeyler,
                        cizgiler: mergedCizgiler,
                        vertex: mergedCizgiNoktalari,
                        faceBrep,
                        edgeBrep: mergedEdgeBrep  // Merged edge BREP bilgisi
                    });
                } catch (err) {
                    reject(err);
                }
            };

            // File tipi kontrolü
            if (dosya instanceof File) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    processJson(e.target?.result as string);
                };
                reader.onerror = (err) => {
                    reject(new Error('Dosya okuma hatası: ' + err));
                };
                reader.readAsText(dosya);
            } else {
                // String veya JSON objesi
                processJson(dosya);
            }
        });
    }


    async alUrlJson(dosya: any): Promise<{
        yuzeyler: THREE.BufferGeometry,
        cizgiler: THREE.BufferGeometry,
        vertex: THREE.BufferGeometry,
        faceBrep?: any[],
        edgeBrep?: any[]
    }[]> {
        return new Promise(async (resolve, reject) => {
            this.noktalar = [];
            this.edges = [];
            this.cizgiNoktalari = [];
            this.yuzeyler = [];

            this.grpi = 0;
            this.strti = 0;
            this.grplinei = 0;
            this.strtlinei = 0;

            const _this = this;
            const _rslt: any[] = [];

            _this.onProgress?.('URL\'den JSON yükleniyor...', 0);

            try {
                const response = await fetch(dosya);
                const json = await response.json();

                const vertices = json.vertices || [];
                const faces = json.faces?.triangles || [];
                const faceColors = json.faces?.color || [];
                const faceAlpha = json.faces?.alpha || [];
                const faceGrup = json.faces?.grup || [];
                const faceBrep = json.faces?.brep || [];

                const edges = json.edges?.lines || [];
                const edgeColors = json.edges?.color || [];
                const edgeAlpha = json.edges?.alpha || [];
                const edgeGrup = json.edges?.grup || [];
                const edgeBrep = json.edges?.brep || [];

                const points = json.points || [];

                _this.onProgress?.('Geometriler oluşturuluyor...', 20);

                // ---------- Yüzeyler ----------
                if (faces.length > 0) {
                    const geom = new THREE.BufferGeometry();
                    const posArray: number[] = [];
                    const colorArray: number[] = [];
                    const alphaArray: number[] = [];
                    const grupArray: number[] = [];

                    for (let i = 0; i < faces.length; i++) {
                        const tri = faces[i];
                        for (let j = 0; j < 3; j++) {
                            const vid = tri[j];
                            const v = vertices[vid];
                            posArray.push(v[0], v[1], v[2]);

                            colorArray.push(
                                faceColors[vid * 3 + 0] ?? 0.8,
                                faceColors[vid * 3 + 1] ?? 0.8,
                                faceColors[vid * 3 + 2] ?? 0.8
                            );
                            alphaArray.push(faceAlpha[vid] ?? 1.0);

                            const gi = vid * 3;
                            grupArray.push(
                                faceGrup[gi + 0] ?? 0,
                                faceGrup[gi + 1] ?? 0,
                                faceGrup[gi + 2] ?? 0
                            );
                        }
                    }

                    geom.setAttribute('position', new THREE.Float32BufferAttribute(posArray, 3));
                    geom.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
                    geom.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArray, 1));
                    geom.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3));

                    this.yuzeyler.push(geom);
                }

                // ---------- Çizgiler ----------
                if (edges.length > 0) {
                    let edgeVertexIndex = 0; // Doğru indexleme için sayaç
                    for (let i = 0; i < edges.length; i++) {
                        const e = edges[i];

                        // Vertex indeksleri kontrol et
                        if (e[0] < 0 || e[1] < 0 || e[0] >= vertices.length || e[1] >= vertices.length) {
                            edgeVertexIndex += 2;
                            continue;
                        }

                        const p0 = vertices[e[0]];
                        const p1 = vertices[e[1]];

                        if (!p0 || !p1) {
                            edgeVertexIndex += 2;
                            continue;
                        }

                        // Segment edge (Python tarafından parçalanmış)
                        const geom = new THREE.BufferGeometry().setFromPoints([
                            new THREE.Vector3(p0[0], p0[1], p0[2]),
                            new THREE.Vector3(p1[0], p1[1], p1[2])
                        ]);

                        const colorArr: number[] = [];
                        const alphaArr: number[] = [];
                        const grupArr: number[] = [];

                        for (let vi = 0; vi < 2; vi++) {
                            colorArr.push(
                                edgeColors[edgeVertexIndex * 3 + 0] ?? 0.2,
                                edgeColors[edgeVertexIndex * 3 + 1] ?? 0.2,
                                edgeColors[edgeVertexIndex * 3 + 2] ?? 0.2
                            );
                            alphaArr.push(edgeAlpha[edgeVertexIndex] ?? 1.0);

                            const gi = edgeVertexIndex * 3;
                            grupArr.push(
                                edgeGrup[gi + 0] ?? 0,
                                edgeGrup[gi + 1] ?? 0,
                                edgeGrup[gi + 2] ?? 0
                            );
                            edgeVertexIndex++;
                        }

                        geom.setAttribute('color', new THREE.Float32BufferAttribute(colorArr, 3));
                        geom.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArr, 1));
                        geom.setAttribute('grup', new THREE.Float32BufferAttribute(grupArr, 3));

                        this.edges.push(geom);

                        this.cizgiNoktalari.push(new THREE.BufferGeometry().setFromPoints([
                            new THREE.Vector3(p0[0], p0[1], p0[2]),
                            new THREE.Vector3(p1[0], p1[1], p1[2])
                        ]));
                    }
                }

                // ---------- Noktalar ----------
                if (points.length > 0) {
                    const geom = new THREE.BufferGeometry();
                    const pos: number[] = [];
                    for (const p of points) pos.push(p[0], p[1], p[2]);
                    geom.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
                    this.noktalar.push(geom);
                }

                _this.onProgress?.('Geometriler birleştiriliyor...', 60);

                const mergedNoktalar = this.noktalar.length > 0 ? BufferGeometryUtils.mergeGeometries(this.noktalar) : new THREE.BufferGeometry();
                const mergedCizgiNoktalari = this.cizgiNoktalari.length > 0 ? BufferGeometryUtils.mergeGeometries(this.cizgiNoktalari) : new THREE.BufferGeometry();
                const mergedCizgiler = this.edges.length > 0 ? BufferGeometryUtils.mergeGeometries(this.edges) : new THREE.BufferGeometry();
                const mergedYuzeyler = this.yuzeyler.length > 0 ? BufferGeometryUtils.mergeGeometries(this.yuzeyler) : new THREE.BufferGeometry();

                _rslt.push({
                    yuzeyler: mergedYuzeyler,
                    cizgiler: mergedCizgiler,
                    vertex: mergedCizgiNoktalari,
                    faceBrep,
                    edgeBrep
                });

                _this.onProgress?.('URL JSON yükleme tamamlandı', 100);

                resolve(_rslt);
            } catch (err) {
                reject(err);
            }
        });
    }



    async alDosyadan(json: any): Promise<{
        yuzeyler: THREE.BufferGeometry,
        cizgiler: THREE.BufferGeometry,
        vertex: THREE.BufferGeometry,
        faceBrep: any[],
        edgeBrep: any[]
    }> {
        return new Promise((resolve) => {
            this.noktalar = [];
            this.edges = [];
            this.cizgiNoktalari = [];
            this.yuzeyler = [];

            this.onProgress?.('JSON yükleniyor...', 0);

            const vertices = json.vertices; // [[x,y,z],...]
            const faces = json.faces.triangles; // [[v0,v1,v2],...]
            const faceColors = json.faces.color; // flattened [r,g,b,r,g,b,...]
            const faceAlpha = json.faces.alpha; // [a,a,...]
            const faceGrup = json.faces.grup;   // [start,count,group,...]
            const faceBrep = json.faces.brep;   // [{...}, ...]

            const edges = json.edges.lines; // [[v0,v1],...]
            const edgeColors = json.edges.color;
            const edgeAlpha = json.edges.alpha;
            const edgeGrup = json.edges.grup;   // [start,count,group,...]
            const edgeBrep = json.edges.brep;   // [{...}, ...]

            const points = json.points; // [[x,y,z],...]

            // ------------------ Yüzeyler ------------------
            if (faces.length > 0) {
                const geometry = new THREE.BufferGeometry();
                const positionArray: number[] = [];
                const colorArray: number[] = [];
                const alphaArray: number[] = [];
                const grupArray: number[] = [];

                for (let i = 0; i < faces.length; i++) {
                    const tri = faces[i];
                    for (let j = 0; j < 3; j++) {
                        const vid = tri[j];
                        const v = vertices[vid];
                        positionArray.push(v[0], v[1], v[2]);

                        colorArray.push(
                            faceColors[vid * 3 + 0] ?? 0.8,
                            faceColors[vid * 3 + 1] ?? 0.8,
                            faceColors[vid * 3 + 2] ?? 0.8
                        );

                        alphaArray.push(faceAlpha[vid] ?? 1.0);

                        // Grup: 3 adet değer per vertex
                        const gi = vid * 3;
                        grupArray.push(
                            faceGrup[gi + 0] ?? 0,
                            faceGrup[gi + 1] ?? 0,
                            faceGrup[gi + 2] ?? 0
                        );
                    }
                }

                geometry.setAttribute('position', new THREE.Float32BufferAttribute(positionArray, 3));
                geometry.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
                geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArray, 1));
                geometry.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3));

                this.yuzeyler.push(geometry);
            }

            // ------------------ Çizgiler ------------------
            if (edges.length > 0) {
                let edgeVertexIndex = 0; // Doğru indexleme için sayaç
                for (let i = 0; i < edges.length; i++) {
                    const e = edges[i];

                    // Vertex indeksleri kontrol et
                    if (e[0] < 0 || e[1] < 0 || e[0] >= vertices.length || e[1] >= vertices.length) {
                        edgeVertexIndex += 2;
                        continue;
                    }

                    const p0 = vertices[e[0]];
                    const p1 = vertices[e[1]];

                    if (!p0 || !p1) {
                        edgeVertexIndex += 2;
                        continue;
                    }

                    const geom = new THREE.BufferGeometry().setFromPoints([
                        new THREE.Vector3(p0[0], p0[1], p0[2]),
                        new THREE.Vector3(p1[0], p1[1], p1[2])
                    ]);

                    // Renk ve alpha
                    const colorArray = [];
                    const alphaArray = [];
                    const grupArray = [];
                    for (let vi = 0; vi < 2; vi++) {
                        colorArray.push(
                            edgeColors[edgeVertexIndex * 3 + 0] ?? 0.2,
                            edgeColors[edgeVertexIndex * 3 + 1] ?? 0.2,
                            edgeColors[edgeVertexIndex * 3 + 2] ?? 0.2
                        );
                        alphaArray.push(edgeAlpha[edgeVertexIndex] ?? 1.0);

                        const gi = edgeVertexIndex * 3;
                        grupArray.push(
                            edgeGrup[gi + 0] ?? 0,
                            edgeGrup[gi + 1] ?? 0,
                            edgeGrup[gi + 2] ?? 0
                        );
                        edgeVertexIndex++;
                    }

                    geom.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));
                    geom.setAttribute('alpha', new THREE.Float32BufferAttribute(alphaArray, 1));
                    geom.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3));

                    this.edges.push(geom);

                    // Edge segment noktaları
                    this.cizgiNoktalari.push(new THREE.BufferGeometry().setFromPoints([
                        new THREE.Vector3(p0[0], p0[1], p0[2]),
                        new THREE.Vector3(p1[0], p1[1], p1[2])
                    ]));
                }
            }

            // ------------------ Noktalar ------------------
            if (points.length > 0) {
                const geom = new THREE.BufferGeometry();
                const positions: number[] = [];
                for (const p of points) {
                    positions.push(p[0], p[1], p[2]);
                }
                geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
                this.noktalar.push(geom);
            }

            // ------------------ Merge ------------------
            const mergedYuzeyler = this.yuzeyler.length > 0 ? BufferGeometryUtils.mergeGeometries(this.yuzeyler) : new THREE.BufferGeometry();
            const mergedCizgiler = this.edges.length > 0 ? BufferGeometryUtils.mergeGeometries(this.edges) : new THREE.BufferGeometry();
            const mergedNoktalar = this.noktalar.length > 0 ? BufferGeometryUtils.mergeGeometries(this.noktalar) : new THREE.BufferGeometry();

            this.onProgress?.('JSON import tamamlandı', 100);

            resolve({
                yuzeyler: mergedYuzeyler,
                cizgiler: mergedCizgiler,
                vertex: mergedNoktalar,
                faceBrep: faceBrep,
                edgeBrep: edgeBrep
            });
        });
    }

    async parcala(dosya: any): Promise<{ yuzeyler: any, cizgiler: any, vertex: any }[]> {
        return new Promise((resolve, reject) => {
            this.noktalar = [];
            this.edges = [];
            this.cizgiNoktalari = [];
            this.yuzeyler = []

            this.grpi = 0;
            this.strti = 0;
            this.grplinei = 0;
            this.strtlinei = 0

            let _this = this;
            let _rslt: any = []

            _this.onProgress?.('WRL parse ediliyor...', 0);

            let loader: VRMLLoader = new VRMLLoader();
            let scene: THREE.Scene = loader.parse(dosya, null);

            _this.onProgress?.('WRL sahne işleniyor...', 20);

            scene.remove(scene.children[0]);

            _this.noktalar = [];
            _this.edges = [];
            _this.cizgiNoktalari = [];
            _this.yuzeyler = []

            scene.traverse(async (grp: any) => {
                if (grp != undefined && grp.type == "Group" && grp?.name != "" && grp?.children?.length > 0) {


                    for (let i = 0; i < grp.children.length; i++) {
                        const element = grp.children[i];
                        /** NOT:  */
                        if (element.type == "Points") {
                            _this.noktaCozumle(element)
                        }
                        /** NOT: */
                        if (element.type == "LineSegments") {
                            _this.cizgiCozumle(element);
                        }
                        if (element.type == "Mesh") {
                            _this.yuzeyCozumle(element)
                        }

                    }

                }
            });

            _this.onProgress?.('Geometriler birleştiriliyor...', 60);

            let mergedCizgiNoktalari = _this.cizgiNoktalari.length > 0 ? BufferGeometryUtils.mergeGeometries(_this.cizgiNoktalari) : new THREE.BufferGeometry();
            let mergedCizgiler = _this.edges.length > 0 ? BufferGeometryUtils.mergeGeometries(_this.edges) : new THREE.BufferGeometry();

            _this.onProgress?.('Yüzeyler birleştiriliyor...', 80);

            let mergedYuzeyler = _this.yuzeyler.length > 0 ? BufferGeometryUtils.mergeGeometries(_this.yuzeyler) : new THREE.BufferGeometry();

            _rslt.push({ yuzeyler: mergedYuzeyler, cizgiler: mergedCizgiler, vertex: mergedCizgiNoktalari })

            _this.onProgress?.('WRL parse tamamlandı', 100);

            resolve(_rslt);

            this.noktalar = null;
            this.edges = null;
            this.cizgiNoktalari = null;
            this.yuzeyler = null;

            for (var i = scene.children.length - 1; i >= 0; i--) {
                let obj = scene.children[i];
                scene.remove(obj);
            }
            loader = null
            scene = null;
            mergedCizgiNoktalari.dispose();
            mergedCizgiNoktalari = null;

            mergedCizgiler.dispose();
            mergedCizgiler = null;

            mergedYuzeyler.dispose();
            mergedYuzeyler = null;

            // _this.sahne?.anaGosterim?.ciz()
            // _this.sahne?.anaGosterim?.FikoYT.degisti.emit();

        });
    }



    noktaCozumle(e: any) {
        this.noktalar.push(e.geometry.clone());
    }

    cizgiCozumle(elm: any) {
        const e = elm as THREE.LineSegments;
        const mat = e.material as THREE.LineBasicMaterial;
        let renkler: any[] = [];
        let seffaflik: any[] = [];
        let grup: any[] = [];
        for (let i = 0; i < e.geometry.attributes['position'].count; i++) {
            renkler.push(...[mat.color.clone().r, mat.color.clone().g, mat.color.clone().b])
            seffaflik.push(mat.opacity);
            grup.push(...[this.strtlinei, e.geometry.attributes['position'].count, this.grplinei])
        }
        e.geometry.setAttribute("color", new THREE.Float32BufferAttribute(renkler, 3));
        e.geometry.setAttribute("alpha", new THREE.Float32BufferAttribute(seffaflik, 1));
        e.geometry.setAttribute("grup", new THREE.Float32BufferAttribute(grup, 3));

        this.edges.push(e.geometry.clone());
        this.grplinei++
        this.strtlinei += e.geometry.attributes['position'].count;
        /** */
        let array = e.geometry.clone().attributes['position'].array;
        let point1: FikoVektor3D = new FikoVektor3D(array[0], array[1], array[2]);
        let point2: FikoVektor3D = new FikoVektor3D(array[array.length - 3], array[array.length - 2], array[array.length - 1]);
        this.cizgiNoktalari.push(new THREE.BufferGeometry().setFromPoints([point1, point2]));
    }

    yuzeyCozumle(elm: any) {
        const e = elm as THREE.Mesh;
        const mat = e.material as THREE.MeshPhongMaterial;
        let renkler: any[] = [];
        let seffaflik: any[] = [];
        let grup: any[] = [];

        for (let i = 0; i < e.geometry.attributes['position'].count; i++) {
            renkler.push(...[mat.color.clone().r, mat.color.clone().g, mat.color.clone().b])
            seffaflik.push(mat.opacity);
            grup.push(...[this.strti, e.geometry.attributes['position'].count, this.grpi])
        }
        e.geometry.setAttribute("color", new THREE.Float32BufferAttribute(renkler, 3));
        e.geometry.setAttribute("alpha", new THREE.Float32BufferAttribute(seffaflik, 1));
        e.geometry.setAttribute("grup", new THREE.Float32BufferAttribute(grup, 3));

        this.yuzeyler.push(e.geometry.clone());
        this.grpi++
        this.strti += e.geometry.attributes['position'].count;
    }




}